// The Money page's write controls: one small "add" form per writable table,
// and a delete button per row.
//
// Deliberately plain. Six people use this app, mid-shift, with a client
// waiting — the live app's own forms are a row of inputs and a button, and
// this is that. No modal, no wizard, no optimistic rendering: the row appears
// when the server says it exists, which is the only moment it does exist.
//
// Three behaviours are load-bearing rather than cosmetic:
//   1. the button disables while a write is in flight, so the second click of
//      a double-click never fires — and if one does, authedWrite's per-action
//      Idempotency-Key means the server replays instead of logging twice;
//   2. a refusal renders the SERVER'S OWN words ("not your clinic", "amount is
//      required and must be a non-negative number"), never a generic message
//      — INV-9, and the difference between a manager fixing the input and a
//      manager filing a bug;
//   3. the clinic list comes from the session, so a manager is never offered a
//      clinic the server will refuse. The server still decides (INV-3); this
//      only keeps the form honest about what it will accept.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { session } from '../lib/session';
import { clinicToday } from '../lib/clinicDay';
import { orderLocations, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { ApiError } from '../lib/apiClient';
import { uploadPettyReceipt, deletePettyReceipt, type PettyReceipt } from '../lib/pettyReceipts';
import {
  addDeposit, addDrawerCount, addPettyCash, addOtherCashItem, updatePettyCash, updateOtherCashItem,
  softDeleteMoneyRow,
  DEPOSIT_METHODS, PETTY_CASH_DIRECTIONS, type MoneyEntity,
  type PettyCashInput, type OtherCashItemInput,
} from '../lib/moneyWrites';

/** The clinics this person may write to, in canonical order (INV-14). */
export function writableLocations(): LocationCode[] {
  const codes = (session.value.me?.locations ?? []) as LocationCode[];
  return orderLocations(codes);
}

/** Today as YYYY-MM-DD at the clinics (Eastern) — the date input's format.
 * Never UTC's day and never the viewer's computer (owner, 2026-10-16). */
export function todayValue(now: Date = new Date()): string {
  return clinicToday(now);
}

/** Shared by every write control in this file, and by the People profile's
 * NHP/notes/rates writes (client/src/pages/People.tsx) — same shape, same
 * busy/error/idempotency discipline, one copy. */
export function useWrite() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(
    fn: () => Promise<unknown>,
    onDone?: () => void,
    opts?: { freeze?: boolean },
  ): Promise<void> {
    const freeze = opts?.freeze !== false;
    if (freeze && busy) return;
    if (freeze) setBusy(true);
    setError(null);
    try {
      await fn();
      onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (freeze) setBusy(false);
    }
  }

  return { busy, error, run };
}

function LocationSelect({ value, onInput }: { value: string; onInput: (v: string) => void }): JSX.Element {
  const codes = writableLocations();
  return (
    <select
      value={value}
      aria-label="Clinic"
      onChange={(e: Event) => onInput((e.target as HTMLSelectElement).value)}
    >
      {codes.map((code) => (
        <option key={code} value={code}>{LOCATION_NAMES[code] ?? code}</option>
      ))}
    </select>
  );
}

/** Shared chrome: the row of controls, the button, and the fault line.
 * `secondary` is an optional extra control after the submit button — used
 * for the "Cancel" button an edit-in-progress form shows (owner rule
 * 2026-09-10: every table has Add and Edit), kept out of `children` so the
 * three existing add-only forms don't have to think about it. */
function FormShell(
  { label, busy, error, onSubmit, children, secondary }:
  {
    label: string; busy: boolean; error: string | null; onSubmit: () => void; children: JSX.Element[];
    secondary?: JSX.Element | null;
  },
): JSX.Element {
  return (
    <form
      class="money-add"
      onSubmit={(e: Event) => { e.preventDefault(); onSubmit(); }}
    >
      {children}
      <button type="submit" class="btn" disabled={busy}>{busy ? 'Saving…' : label}</button>
      {secondary ?? null}
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </form>
  );
}

export function AddDepositForm(): JSX.Element {
  const codes = writableLocations();
  const [locationCode, setLocation] = useState<string>(codes[0] ?? '');
  const [depositDate, setDate] = useState(todayValue());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<string>(DEPOSIT_METHODS[0]);
  const { busy, error, run } = useWrite();

  return (
    <FormShell
      label="Add deposit"
      busy={busy}
      error={error}
      onSubmit={() => void run(
        () => addDeposit({ locationCode, depositDate, amount, method }),
        () => setAmount(''),
      )}
    >
      <LocationSelect value={locationCode} onInput={setLocation} />
      <input
        type="date" value={depositDate} aria-label="Deposit date"
        onInput={(e: Event) => setDate((e.target as HTMLInputElement).value)}
      />
      <input
        type="number" step="0.01" min="0" value={amount} placeholder="Amount" aria-label="Amount"
        onInput={(e: Event) => setAmount((e.target as HTMLInputElement).value)}
      />
      <select
        value={method} aria-label="Method"
        onChange={(e: Event) => setMethod((e.target as HTMLSelectElement).value)}
      >
        {DEPOSIT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </FormShell>
  );
}

export function AddDrawerCountForm(): JSX.Element {
  const codes = writableLocations();
  const [locationCode, setLocation] = useState<string>(codes[0] ?? '');
  const [countDate, setDate] = useState(todayValue());
  const [countedAmount, setCounted] = useState('');
  const [expectedAmount, setExpected] = useState('');
  const { busy, error, run } = useWrite();

  return (
    <FormShell
      label="Add drawer count"
      busy={busy}
      error={error}
      onSubmit={() => void run(
        () => addDrawerCount({ locationCode, countDate, countedAmount, expectedAmount }),
        () => { setCounted(''); setExpected(''); },
      )}
    >
      <LocationSelect value={locationCode} onInput={setLocation} />
      <input
        type="date" value={countDate} aria-label="Count date"
        onInput={(e: Event) => setDate((e.target as HTMLInputElement).value)}
      />
      <input
        type="number" step="0.01" min="0" value={countedAmount} placeholder="Counted" aria-label="Counted amount"
        onInput={(e: Event) => setCounted((e.target as HTMLInputElement).value)}
      />
      <input
        type="number" step="0.01" min="0" value={expectedAmount} placeholder="Expected" aria-label="Expected amount"
        onInput={(e: Event) => setExpected((e.target as HTMLInputElement).value)}
      />
      {/* Variance is NOT an input. The server computes it (SPEC-001 Phase 5:
          "variance is computed server-side"), so a hand-typed one could
          disagree with the arithmetic — which is the whole reason it is not
          offered here. */}
    </FormShell>
  );
}

/** The subset of a petty-cash row `startEdit` needs to prefill the form —
 * structurally compatible with Money.tsx's own (wider) `PettyCashRow`, kept
 * local rather than imported from pages/Money.tsx (which itself imports
 * this form) to avoid a needless import cycle. */
export interface PettyCashEditable {
  id: number;
  locationCode: string;
  transactionDate: string;
  direction: string;
  amount: string | number;
  reason: string | null;
}

/**
 * Add OR edit one petty cash row (owner rule 2026-09-10: every table has Add
 * and Edit). Passing `editing` switches this from the add form into the same
 * form pre-filled with that row's values, the button reads "Save" instead of
 * "Add petty cash", and submit calls `updatePettyCash` instead of
 * `addPettyCash` — the same shared-form idiom CompanyLicenses.tsx's
 * `startEdit` uses, just kept local to this one small form rather than
 * lifted into the page. The caller (Money.tsx) is expected to pass a
 * `key` that changes with the editing target, so switching rows (or from
 * edit back to add) remounts this component with fresh initial state
 * instead of trying to reconcile old input values against a new row.
 */
export function AddPettyCashForm(
  { editing = null, onDone }: { editing?: PettyCashEditable | null; onDone?: () => void } = {},
): JSX.Element {
  const codes = writableLocations();
  const [locationCode, setLocation] = useState<string>(editing?.locationCode ?? codes[0] ?? '');
  const [transactionDate, setDate] = useState(editing?.transactionDate ?? todayValue());
  const [direction, setDirection] = useState<string>(editing?.direction ?? 'OUT');
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '');
  const [reason, setReason] = useState(editing?.reason ?? '');
  const { busy, error, run } = useWrite();

  function submit(): void {
    const input: PettyCashInput = {
      locationCode, transactionDate, direction, amount, reason,
    };
    void run(
      () => (editing ? updatePettyCash(editing.id, input) : addPettyCash(input)),
      () => {
        if (editing) onDone?.();
        else { setAmount(''); setReason(''); }
      },
    );
  }

  return (
    <FormShell
      label={editing ? 'Save' : 'Add petty cash'}
      busy={busy}
      error={error}
      onSubmit={submit}
      secondary={editing ? (
        <button type="button" class="btn" disabled={busy} onClick={() => onDone?.()}>Cancel</button>
      ) : null}
    >
      <LocationSelect value={locationCode} onInput={setLocation} />
      <input
        type="date" value={transactionDate} aria-label="Transaction date"
        onInput={(e: Event) => setDate((e.target as HTMLInputElement).value)}
      />
      <select
        value={direction} aria-label="Direction"
        onChange={(e: Event) => setDirection((e.target as HTMLSelectElement).value)}
      >
        {PETTY_CASH_DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <input
        type="number" step="0.01" min="0" value={amount} placeholder="Amount" aria-label="Amount"
        onInput={(e: Event) => setAmount((e.target as HTMLInputElement).value)}
      />
      <input
        type="text" value={reason} placeholder="Reason" aria-label="Reason"
        onInput={(e: Event) => setReason((e.target as HTMLInputElement).value)}
      />
    </FormShell>
  );
}

/** Same idea as PettyCashEditable above, for the Other Cash Items form. */
export interface OtherCashItemEditable {
  id: number;
  locationCode: string;
  itemDate: string;
  direction: string;
  amount: string | number;
  description: string | null;
}

/** Add OR edit one other-cash-item row — same `editing`/`onDone` shape as
 * AddPettyCashForm above (owner rule 2026-09-10: every table has Add and
 * Edit). */
export function AddOtherCashItemForm(
  { editing = null, onDone }: { editing?: OtherCashItemEditable | null; onDone?: () => void } = {},
): JSX.Element {
  const codes = writableLocations();
  const [locationCode, setLocation] = useState<string>(editing?.locationCode ?? codes[0] ?? '');
  const [itemDate, setDate] = useState(editing?.itemDate ?? todayValue());
  const [direction, setDirection] = useState<string>(editing?.direction ?? 'IN');
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const { busy, error, run } = useWrite();

  function submit(): void {
    const input: OtherCashItemInput = {
      locationCode, itemDate, direction, amount, description,
    };
    void run(
      () => (editing ? updateOtherCashItem(editing.id, input) : addOtherCashItem(input)),
      () => {
        if (editing) onDone?.();
        else { setAmount(''); setDescription(''); }
      },
    );
  }

  return (
    <FormShell
      label={editing ? 'Save' : 'Add other cash item'}
      busy={busy}
      error={error}
      onSubmit={submit}
      secondary={editing ? (
        <button type="button" class="btn" disabled={busy} onClick={() => onDone?.()}>Cancel</button>
      ) : null}
    >
      <LocationSelect value={locationCode} onInput={setLocation} />
      <input
        type="date" value={itemDate} aria-label="Item date"
        onInput={(e: Event) => setDate((e.target as HTMLInputElement).value)}
      />
      <select
        value={direction} aria-label="Direction"
        onChange={(e: Event) => setDirection((e.target as HTMLSelectElement).value)}
      >
        {PETTY_CASH_DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <input
        type="number" step="0.01" min="0" value={amount} placeholder="Amount" aria-label="Amount"
        onInput={(e: Event) => setAmount((e.target as HTMLInputElement).value)}
      />
      <input
        type="text" value={description} placeholder="Description" aria-label="Description"
        onInput={(e: Event) => setDescription((e.target as HTMLInputElement).value)}
      />
    </FormShell>
  );
}

/** Petty cash receipts (idea 38): a paperclip with the count on the row; click
 * to open the list (each opens its file, with Remove) and an upload that takes
 * several photos or PDFs at once. */
export function PettyReceipts({ id, receipts }: { id: number; receipts: PettyReceipt[] }): JSX.Element {
  const { busy, error, run } = useWrite();
  const [open, setOpen] = useState(false);

  async function onPick(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    await run(async () => {
      for (const file of files) {
        // One after another: the first failure stops the rest and shows its reason.
        // eslint-disable-next-line no-await-in-loop
        await uploadPettyReceipt(id, file);
      }
    });
  }

  return (
    <span class="petty-receipts">
      <button
        type="button"
        class="btn"
        data-testid={`petty-receipts-${id}`}
        aria-label={`Receipts for petty cash ${id}: ${receipts.length}`}
        onClick={() => setOpen(!open)}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M21.4 11.1l-9.2 9.2a5.5 5.5 0 0 1-7.8-7.8l9.2-9.2a3.7 3.7 0 0 1 5.2 5.2l-9.2 9.2a1.8 1.8 0 0 1-2.6-2.6l8.5-8.5" />
        </svg>{' '}
        {receipts.length}
      </button>
      {open ? (
        <div class="petty-receipts-list">
          {receipts.map((r) => (
            <div key={r.id} data-testid={`petty-receipt-${r.id}`}>
              <a href={r.url} target="_blank" rel="noopener noreferrer">{r.name || 'Receipt'}</a>{' '}
              <button
                type="button"
                class="btn"
                disabled={busy}
                aria-label={`Remove receipt ${r.id}`}
                onClick={() => void run(() => deletePettyReceipt(id, r.id))}
              >
                Remove
              </button>
            </div>
          ))}
          <label>
            {busy ? 'Uploading…' : 'Add receipt (photo or PDF)'}{' '}
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              data-testid={`petty-receipt-input-${id}`}
              disabled={busy}
              onChange={(e: Event) => void onPick(e)}
            />
          </label>
          {error ? <small class="bad" role="alert">{error}</small> : null}
        </div>
      ) : null}
    </span>
  );
}

/**
 * Delete one money row. Soft, always — the server flips a flag and keeps the
 * row (migration 020), so this is not the irreversible thing the word
 * "delete" usually means. It still asks first: a mis-click on a row of
 * yesterday's cash is worth one confirm.
 *
 * Inline "Delete? Yes / Cancel", the same idiom as People's notes, the task
 * notes and the ideas page. It replaced `window.confirm` on 2026-08-17: a
 * native dialog freezes the whole tab to automation (docs/known-traps.md), and
 * this was the LAST one in the client — a leftover test row sat in production
 * for two days because no session could dismiss the box to delete it.
 */
export function DeleteMoneyRowButton({ entity, id }: { entity: MoneyEntity; id: number }): JSX.Element {
  const { busy, error, run } = useWrite();
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <span data-testid={`delete-${entity}-${id}-confirm`}>
        Delete?{' '}
        <button
          type="button"
          class="btn"
          disabled={busy}
          aria-label={`Confirm delete ${entity} ${id}`}
          onClick={() => void run(() => softDeleteMoneyRow(entity, id))}
        >
          {busy ? 'Deleting…' : 'Yes'}
        </button>{' '}
        <button type="button" class="btn" disabled={busy} onClick={() => setConfirming(false)}>
          Cancel
        </button>
        {error ? <small class="bad" role="alert">{error}</small> : null}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        class="btn"
        disabled={busy}
        aria-label={`Delete ${entity} ${id}`}
        onClick={() => setConfirming(true)}
      >
        Delete
      </button>
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </>
  );
}
